import { useState, useRef, useEffect } from "react";
import { MapPin, Check, AlertCircle } from "lucide-react";
import { SA_SUBURBS } from "@/data/sa_suburbs";

export default function SuburbField({ value, onChange }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = q ? SA_SUBURBS.filter((s) => s.toLowerCase().includes(q)).slice(0, 6) : SA_SUBURBS.slice(0, 6);
  const serviceable = SA_SUBURBS.some((s) => s.toLowerCase() === q);
  const showNotAvailable = query.trim().length > 1 && !serviceable;

  const pick = (s) => {
    setQuery(s);
    onChange(s, true);
    setOpen(false);
  };

  const handleInput = (v) => {
    setQuery(v);
    setOpen(true);
    setActive(0);
    onChange(v, SA_SUBURBS.some((s) => s.toLowerCase() === v.trim().toLowerCase()));
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && matches[active]) { e.preventDefault(); pick(matches[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D9488]" />
        <input
          data-testid="quote-suburb"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Type your suburb"
          autoComplete="off"
          className="w-full h-12 rounded-xl border border-slate-200 bg-[#FAFAF9] pl-9 pr-3 text-sm text-[#1C2A39] outline-none focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/20 transition"
        />
      </div>

      {open && matches.length > 0 && (
        <ul data-testid="suburb-options" className="absolute z-50 mt-2 w-full max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1">
          {matches.map((s, i) => (
            <li key={s}>
              <button
                type="button"
                data-testid={`suburb-option-${i}`}
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors ${
                  i === active ? "bg-[#E8F1EB] text-[#1C2A39]" : "text-[#475569] hover:bg-slate-50"
                }`}
              >
                {s} {query.trim().toLowerCase() === s.toLowerCase() && <Check size={15} className="text-[#0D9488]" />}
              </button>
            </li>
          ))}
        </ul>
      )}

      {showNotAvailable && (
        <p data-testid="suburb-not-available" className="mt-2 flex items-center gap-1.5 text-sm text-red-600">
          <AlertCircle size={14} /> Sorry, we don't currently service this location.
        </p>
      )}
      {serviceable && query && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-[#0D9488]">
          <Check size={14} /> Great — we service {query}!
        </p>
      )}
    </div>
  );
}
